// Marketing · Dashboard: conversión web, contactos creados por día (origen web vs otros),
// contactos por canal, top del periodo y actividades del usuario (vencidas / hoy / próximas).
// Gráficos en SVG propio (sin dependencias), con tokens de color para modo claro/oscuro.
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Calendar, CheckSquare } from "lucide-react";
import { api } from "../../lib/api.ts";
import { formatDate, formatDateTime, formatMoney } from "../../lib/format.ts";
import { Card, ErrorState, Input, Skeleton } from "../../components/ui.tsx";
import { toast } from "../../components/toast.tsx";
import { errMsg } from "./contacts/shared.tsx";

interface Activity {
  kind: "task" | "meeting"; id: string; title: string; at: string; priority: string | null;
  relatedModule: string | null; relatedId: string | null; relatedName: string | null;
}
interface TopRow { id: string; name: string; n: number }
interface Dashboard {
  range: { from: string; to: string };
  funnel: { visitors: number; contacts: number; opportunities: number; sales: number; salesAmount: string;
    rates: { visitorsToContacts: number | null; contactsToOpportunities: number | null; opportunitiesToSales: number | null } };
  contactsByDay: Array<{ day: string; web: number; other: number }>;
  contactsByChannel: Array<{ channel: string; n: number; web: boolean }>;
  top: { forms: TopRow[]; fbForms: TopRow[]; landings: TopRow[]; surveys: TopRow[];
    campaigns: Array<{ id: string; name: string; sentAt: string; stats: Record<string, number> }> };
  activities: { overdue: Activity[]; today: Activity[]; upcoming: Activity[]; totals: Record<string, number> };
}

// Paleta categórica validada (2 series) para superficie clara y oscura.
const WEB = "fill-[#2a78d6] dark:fill-[#3987e5]";
const OTHER = "fill-[#eb6834] dark:fill-[#d95926]";
const WEB_BG = "bg-[#2a78d6] dark:bg-[#3987e5]";
const OTHER_BG = "bg-[#eb6834] dark:bg-[#d95926]";

const today = (): string => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };
const minus = (d: string, n: number): string => new Date(Date.parse(`${d}T00:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
const nf = (n: number): string => n.toLocaleString("es-CO");
// Más de 100 % no es una conversión real (p. ej. contactos importados frente a pocas visitas web): se muestra "—".
const pct = (v: number | null): string => (v == null || v > 100 ? "—" : `${v.toLocaleString("es-CO", { maximumFractionDigits: 2 })} %`);

export function MktDashboard(): JSX.Element {
  const [preset, setPreset] = useState<"7" | "30" | "90" | "custom">("30");
  const [from, setFrom] = useState(minus(today(), 29));
  const [to, setTo] = useState(today());

  const range = preset === "custom" ? { from, to } : { from: minus(today(), Number(preset) - 1), to: today() };
  const dash = useQuery<Dashboard>({
    queryKey: ["mkt", "dashboard", range.from, range.to],
    queryFn: () => api.get(`/mkt/dashboard?from=${range.from}&to=${range.to}&base=created`),
    placeholderData: keepPreviousData,
    enabled: range.from <= range.to,
  });
  const d = dash.data;

  return (
    <div className="space-y-4">
      {/* Filtros en una sola fila */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-line p-0.5 bg-surface" role="group" aria-label="Rango de fechas">
          {(["7", "30", "90"] as const).map((p) => (
            <button key={p} type="button" aria-pressed={preset === p} onClick={() => setPreset(p)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${preset === p ? "bg-primary-soft text-primary dark:text-ink font-medium" : "text-ink-secondary hover:text-ink"}`}>
              {p} días
            </button>
          ))}
          <button type="button" aria-pressed={preset === "custom"} onClick={() => { setFrom(range.from); setTo(range.to); setPreset("custom"); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${preset === "custom" ? "bg-primary-soft text-primary dark:text-ink font-medium" : "text-ink-secondary hover:text-ink"}`}>
            Personalizado
          </button>
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" className="w-auto" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="Desde" />
            <span className="text-ink-muted">–</span>
            <Input type="date" className="w-auto" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="Hasta" />
          </div>
        )}
        <span className="text-xs text-ink-muted">Base: contactos creados · {formatDate(range.from)} – {formatDate(range.to)}</span>
        {dash.isFetching && !dash.isLoading && <span className="text-xs text-ink-muted">Actualizando…</span>}
      </div>

      {dash.isError && <Card><ErrorState message={errMsg(dash.error, "No se pudo cargar el dashboard.")} onRetry={() => void dash.refetch()} /></Card>}

      {/* Conversión web */}
      <Card className="p-5">
        <h3 className="sec-title mb-4">Conversión web</h3>
        {!d ? <Skeleton className="h-20 w-full" /> : (
          <div className="grid grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-3">
            <FunnelStep label="Visitantes" value={d.funnel.visitors} />
            <FunnelArrow rate={d.funnel.rates.visitorsToContacts} />
            <FunnelStep label="Contactos" value={d.funnel.contacts} />
            <FunnelArrow rate={d.funnel.rates.contactsToOpportunities} />
            <FunnelStep label="Oportunidades" value={d.funnel.opportunities} />
            <FunnelArrow rate={d.funnel.rates.opportunitiesToSales} />
            <FunnelStep label="Ventas" value={d.funnel.sales} sub={Number(d.funnel.salesAmount) > 0 ? formatMoney(d.funnel.salesAmount) : undefined} />
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-5 xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="sec-title">Contactos creados</h3>
            <div className="flex items-center gap-4 text-xs text-ink-secondary" aria-hidden>
              <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${WEB_BG}`} />Origen web</span>
              <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${OTHER_BG}`} />Otros orígenes</span>
            </div>
          </div>
          {!d ? <Skeleton className="h-56 w-full" /> : <DailyChart days={d.contactsByDay} />}
        </Card>

        <Card className="p-5">
          <h3 className="sec-title mb-3">Contactos creados por tipo de canal</h3>
          {!d ? <Skeleton className="h-40 w-full" /> : <ChannelBars rows={d.contactsByChannel} />}
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ActivitiesPanel data={d?.activities} />
        <Card className="p-5 xl:col-span-2">
          <h3 className="sec-title mb-3">Top del periodo</h3>
          {!d ? <Skeleton className="h-40 w-full" /> : <TopBlocks top={d.top} />}
        </Card>
      </div>
    </div>
  );
}

function FunnelStep({ label, value, sub }: { label: string; value: number; sub?: string | undefined }): JSX.Element {
  return (
    <div className="rounded-lg border border-line px-4 py-3 text-center">
      <div className="dt-label">{label}</div>
      <div className="text-2xl font-semibold text-ink tnum mt-1">{nf(value)}</div>
      {sub && <div className="text-xs text-ink-muted tnum">{sub}</div>}
    </div>
  );
}
function FunnelArrow({ rate }: { rate: number | null }): JSX.Element {
  return (
    <div className="hidden lg:flex flex-col items-center text-ink-muted" aria-label={`Conversión ${pct(rate)}`}>
      <span className="text-xs font-medium text-ink-secondary tnum">{pct(rate)}</span>
      <ArrowRight size={18} />
    </div>
  );
}

/** Barras diarias apiladas (web abajo, otros arriba), con tooltip y tabla accesible. */
function DailyChart({ days }: { days: Dashboard["contactsByDay"] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720; const H = 220; const padL = 36; const padB = 22; const padT = 8;
  const max = Math.max(1, ...days.map((d) => d.web + d.other));
  const nice = (() => { const p = 10 ** Math.floor(Math.log10(max)); const m = max / p; return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p; })();
  const plotW = W - padL; const plotH = H - padB - padT;
  const slot = plotW / Math.max(1, days.length);
  const bw = Math.max(2, Math.min(28, slot - 2));
  const y = (v: number): number => padT + plotH - (v / nice) * plotH;
  const labelEvery = Math.ceil(days.length / 8);
  const total = days.reduce((a, d) => a + d.web + d.other, 0);
  const h = hover != null ? days[hover] : null;

  return (
    <div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
          aria-label={`Contactos creados por día: ${nf(total)} en el periodo`} onMouseLeave={() => setHover(null)}>
          {[0, 0.5, 1].map((t) => (
            <g key={t}>
              <line x1={padL} x2={W} y1={y(nice * t)} y2={y(nice * t)} className="stroke-[var(--border)]" strokeWidth={1} />
              <text x={padL - 6} y={y(nice * t) + 4} textAnchor="end" className="fill-[var(--text-muted)] text-[11px] tnum">{nf(nice * t)}</text>
            </g>
          ))}
          {days.map((d, i) => {
            const x = padL + i * slot + (slot - bw) / 2;
            const yWeb = y(d.web); const yTop = y(d.web + d.other);
            const gap = d.web > 0 && d.other > 0 ? 2 : 0;
            return (
              <g key={d.day}>
                {d.web > 0 && <rect x={x} y={yWeb} width={bw} height={y(0) - yWeb} rx={Math.min(3, bw / 3)} className={WEB} />}
                {d.other > 0 && <rect x={x} y={yTop} width={bw} height={Math.max(0, yWeb - yTop - gap)} rx={Math.min(3, bw / 3)} className={OTHER} />}
                {/* Zona de hover más grande que la barra */}
                <rect x={padL + i * slot} y={padT} width={slot} height={plotH} fill="transparent"
                  onMouseEnter={() => setHover(i)} className={hover === i ? "fill-[var(--text)] opacity-[0.04]" : ""} />
                {i % labelEvery === 0 && (
                  <text x={padL + i * slot + slot / 2} y={H - 6} textAnchor="middle" className="fill-[var(--text-muted)] text-[11px]">
                    {d.day.slice(8, 10)}/{d.day.slice(5, 7)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {h && hover != null && (
          <div className="pointer-events-none absolute top-0 card shadow-md px-3 py-2 text-xs"
            style={{ left: `${Math.min(80, ((padL + hover * slot + slot / 2) / W) * 100)}%` }}>
            <div className="font-medium text-ink mb-1">{formatDate(h.day)}</div>
            <div className="flex items-center gap-1.5 text-ink-secondary"><span className={`w-2 h-2 rounded-sm ${WEB_BG}`} />Origen web <b className="ml-auto pl-3 text-ink tnum">{h.web}</b></div>
            <div className="flex items-center gap-1.5 text-ink-secondary"><span className={`w-2 h-2 rounded-sm ${OTHER_BG}`} />Otros <b className="ml-auto pl-3 text-ink tnum">{h.other}</b></div>
          </div>
        )}
      </div>
      <details className="mt-2 text-xs text-ink-secondary">
        <summary className="cursor-pointer text-ink-muted">Ver tabla</summary>
        <div className="max-h-56 overflow-y-auto mt-2">
          <table className="w-full">
            <thead><tr><th className="th">Día</th><th className="th text-right">Origen web</th><th className="th text-right">Otros</th></tr></thead>
            <tbody>{days.filter((d) => d.web || d.other).map((d) => (
              <tr key={d.day}><td className="td py-1.5">{formatDate(d.day)}</td><td className="td py-1.5 text-right tnum">{d.web}</td><td className="td py-1.5 text-right tnum">{d.other}</td></tr>
            ))}</tbody>
          </table>
          {total === 0 && <p className="py-2 text-ink-muted">Sin contactos creados en el periodo.</p>}
        </div>
      </details>
    </div>
  );
}

function ChannelBars({ rows }: { rows: Dashboard["contactsByChannel"] }): JSX.Element {
  if (rows.length === 0) return <p className="text-sm text-ink-muted py-6 text-center">Sin contactos creados en el periodo.</p>;
  const max = Math.max(...rows.map((r) => r.n));
  const total = rows.reduce((a, r) => a + r.n, 0);
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.channel}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-ink-secondary">{r.channel}</span>
            <span className="text-ink font-medium tnum">{nf(r.n)} <span className="text-ink-muted font-normal">· {Math.round((r.n / total) * 100)} %</span></span>
          </div>
          <div className="h-2 rounded-full bg-surface-hover overflow-hidden">
            <div className={`h-full rounded-full ${r.web ? WEB_BG : OTHER_BG}`} style={{ width: `${Math.max(2, (r.n / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function TopBlocks({ top }: { top: Dashboard["top"] }): JSX.Element {
  const blocks: Array<{ title: string; rows: Array<{ id: string; name: string; value: string }>; unit: string }> = [
    { title: "Formularios", unit: "envíos", rows: top.forms.map((r) => ({ id: r.id, name: r.name, value: nf(r.n) })) },
    { title: "Facebook Lead Ads", unit: "leads", rows: top.fbForms.map((r) => ({ id: r.id, name: r.name, value: nf(r.n) })) },
    { title: "Landing pages", unit: "visitas", rows: top.landings.map((r) => ({ id: r.id, name: r.name, value: nf(r.n) })) },
    { title: "Encuestas", unit: "respuestas", rows: top.surveys.map((r) => ({ id: r.id, name: r.name, value: nf(r.n) })) },
    { title: "Campañas enviadas", unit: "aperturas únicas", rows: top.campaigns.map((c) => ({ id: c.id, name: c.name, value: nf(Number(c.stats["uniqueOpens"] ?? 0)) })) },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {blocks.map((b) => (
        <div key={b.title}>
          <div className="flex justify-between items-baseline mb-1.5">
            <h4 className="text-sm font-medium text-ink">{b.title}</h4>
            <span className="text-[11px] text-ink-muted">{b.unit}</span>
          </div>
          {b.rows.length === 0 ? <p className="text-xs text-ink-muted">Sin datos en el periodo.</p> : (
            <ol className="space-y-1">
              {b.rows.map((r) => (
                <li key={r.id} className="flex justify-between gap-3 text-sm">
                  <span className="text-ink-secondary truncate">{r.name}</span><span className="text-ink tnum font-medium">{r.value}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  );
}

const REL_PATH: Record<string, string> = { contacts: "/contacts", leads: "/leads", accounts: "/accounts", deals: "/deals" };

function ActivitiesPanel({ data }: { data: Dashboard["activities"] | undefined }): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"overdue" | "today" | "upcoming">("today");
  const [done, setDone] = useState<Set<string>>(new Set());
  const complete = useMutation({
    mutationFn: (id: string) => api.patch(`/tasks/${id}`, { status: "Completada" }),
    onSuccess: (_r, id) => { setDone((p) => new Set(p).add(id)); toast.success("Tarea completada."); void qc.invalidateQueries({ queryKey: ["mkt", "dashboard"] }); },
    onError: (e) => toast.error(errMsg(e, "No se pudo completar la tarea.")),
  });
  const tabs = useMemo(() => [
    { k: "overdue" as const, l: "Vencidas", n: data?.totals["overdue"] ?? 0 },
    { k: "today" as const, l: "Para hoy", n: data?.totals["today"] ?? 0 },
    { k: "upcoming" as const, l: "Próximas", n: data?.totals["upcoming"] ?? 0 },
  ], [data]);
  const items = (data?.[tab] ?? []).filter((a) => !done.has(a.id));

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="sec-title">Mis actividades</h3>
        <Link to="/activities" className="link text-xs">Ver todas</Link>
      </div>
      <div className="flex gap-1 mb-3" role="group" aria-label="Actividades">
        {tabs.map((t) => (
          <button key={t.k} type="button" aria-pressed={tab === t.k} onClick={() => setTab(t.k)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${tab === t.k ? "bg-primary-soft border-primary text-primary dark:text-ink" : "border-line text-ink-secondary hover:bg-surface-hover"}`}>
            {t.l} <span className={`tnum ${t.k === "overdue" && t.n > 0 ? "text-danger font-semibold" : ""}`}>{t.n}</span>
          </button>
        ))}
      </div>
      {data && (data.totals[tab] ?? 0) > (data[tab]?.length ?? 0) && (
        <p className="text-xs text-ink-muted mb-1">Mostrando {data[tab].length} de {nf(data.totals[tab] ?? 0)}.</p>
      )}
      {!data ? <Skeleton className="h-32 w-full" /> : items.length === 0 ? (
        <p className="text-sm text-ink-muted py-6 text-center">Nada pendiente aquí.</p>
      ) : (
        <ul className="divide-y divide-[var(--border)] max-h-96 overflow-y-auto">
          {items.map((a) => (
            <li key={`${a.kind}-${a.id}`} className="py-2.5 flex items-start gap-2.5">
              {a.kind === "task" ? (
                <input type="checkbox" className="mt-1" disabled={complete.isPending} onChange={() => complete.mutate(a.id)} aria-label={`Completar ${a.title}`} />
              ) : <Calendar size={16} className="mt-0.5 text-ink-muted shrink-0" aria-label="Reunión" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink truncate flex items-center gap-1.5">
                  {a.kind === "task" && <CheckSquare size={13} className="text-ink-muted shrink-0" aria-hidden />}{a.title}
                </div>
                <div className="text-xs text-ink-muted">
                  <span className={tab === "overdue" ? "text-danger" : ""}>{a.kind === "task" ? formatDate(a.at) : formatDateTime(a.at)}</span>
                  {a.relatedId && a.relatedModule && REL_PATH[a.relatedModule] && (
                    <> · <Link className="link" to={`${REL_PATH[a.relatedModule]}/${a.relatedId}`}>{a.relatedName ?? "Ver registro"}</Link></>
                  )}
                  {a.priority === "Alta" && <span className="ml-1 badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">Alta</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
